import React, { useEffect, useState, useCallback } from 'react';
import { Smartphone, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { pushSupported, currentSubscription, enablePush, disablePush, sendTestPush } from '../utils/push';

const optOutKey = () => `push_opt_out_${localStorage.getItem('username') || ''}`;
const errorText = (e) => {
  const raw = e.response?.data?.detail || e.message || 'Could not change notification setting';
  return /registration failed|push service|AbortError/i.test(raw)
    ? 'This browser could not register for push (blocked push service or unsupported browser). Use Chrome, Edge or Firefox; on iPhone add the site to your Home Screen first.'
    : raw;
};

// Acacia office only. Alerts are ON by default on every device: auto-subscribes when permission is granted,
// otherwise shows a one-click prompt. Turning it off is remembered per device until turned on again.
export default function PushToggle({ authCfg }) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const supported = pushSupported();

  const enable = useCallback(async (silent) => {
    setBusy(true);
    try {
      await enablePush(authCfg);
      localStorage.removeItem(optOutKey());
      setEnabled(true);
      setPrompt(false);
      if (!silent) toast.success('Approval alerts ON — you will be notified on this device when a hospital sends a request for approval, even when the app is closed.', { duration: 7000 });
      return true;
    } catch (e) {
      if (!silent) toast.error(errorText(e), { duration: 8000 });
      return false;
    } finally {
      setBusy(false);
    }
  }, [authCfg]);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      const sub = await currentSubscription().catch(() => null);
      if (cancelled) return;
      if (sub) { setEnabled(true); return; }
      if (localStorage.getItem(optOutKey())) return; // user turned it off on this device
      if (Notification.permission === 'granted') { await enable(true); return; }
      if (Notification.permission === 'default') {
        // Default ON: try the permission prompt right away; fall back to a one-click banner if the browser needs a gesture.
        const ok = await enable(true);
        if (!ok && !cancelled && Notification.permission !== 'denied') setPrompt(true);
      }
    })();
    return () => { cancelled = true; };
  }, [supported, enable]);

  const toggle = async () => {
    if (enabled) {
      setBusy(true);
      try {
        await disablePush(authCfg);
        localStorage.setItem(optOutKey(), '1');
        setEnabled(false);
        toast.info('Approval alerts turned off on this device. Click the bell to turn them back on.');
      } catch (e) {
        toast.error(errorText(e), { duration: 8000 });
      } finally {
        setBusy(false);
      }
    } else {
      await enable(false);
    }
  };

  const test = async () => {
    try {
      const { data } = await sendTestPush(authCfg);
      toast.success(data.sent > 0 ? 'Test alert sent — check your notifications' : 'No subscribed devices found for your account');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    }
  };

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const explainUnsupported = () => toast.info(
    isIOS
      ? 'To receive alerts on iPhone/iPad: open this site in Safari, tap Share → "Add to Home Screen", then open Acacia from the Home Screen and slide Alerts ON.'
      : 'This browser cannot receive push alerts. Use Chrome, Edge or Firefox (or install the site to your Home Screen) and slide Alerts ON there.',
    { duration: 10000 }
  );

  if (!supported) {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer select-none py-2" title="Alerts are not available in this browser — tap for help" data-testid="push-unsupported">
        <button type="button" role="switch" onClick={explainUnsupported} className="relative inline-flex h-7 w-12 items-center rounded-full bg-gray-200 flex-shrink-0" aria-checked={false} data-testid="push-toggle">
          <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow translate-x-1" />
        </button>
        <span className="text-sm font-medium text-gray-400 whitespace-nowrap" data-testid="push-toggle-label">Alerts OFF</span>
      </label>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <label className="inline-flex items-center gap-2 cursor-pointer select-none py-2" title={enabled ? 'Approval alerts are ON for this device — slide to turn off' : 'Slide to get a notification on this device when a hospital sends a request for approval'}>
        <button
          type="button"
          role="switch"
          onClick={toggle}
          disabled={busy}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 disabled:opacity-60 ${enabled ? 'bg-teal-600' : 'bg-gray-300'}`}
          data-testid="push-toggle"
          aria-pressed={enabled}
          aria-checked={enabled}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
        <span className={`text-sm font-medium whitespace-nowrap ${enabled ? 'text-teal-700' : 'text-gray-600'}`} data-testid="push-toggle-label">
          {busy ? 'Alerts…' : enabled ? 'Alerts ON' : 'Alerts OFF'}
        </span>
      </label>
      {enabled && (
        <button onClick={test} className="text-[10px] text-gray-500 hover:text-teal-700 underline hidden md:inline" data-testid="push-test">test</button>
      )}
      {prompt && !enabled && (
        <div className="fixed bottom-4 right-4 z-[150] max-w-sm bg-white border-2 border-teal-300 rounded-xl shadow-2xl p-4 flex gap-3 items-start" data-testid="push-prompt">
          <Smartphone className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-800 flex-1">
            <p className="font-bold">Turn on approval alerts on this device</p>
            <p className="text-xs text-gray-600 mt-0.5">Get notified when a hospital sends a pre-authorization, test, prescription or admission request — even when the app is closed.</p>
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={() => enable(false)} disabled={busy} className="bg-teal-600 hover:bg-teal-700 h-8" data-testid="push-prompt-enable">Turn on</Button>
              <Button size="sm" variant="outline" onClick={() => { localStorage.setItem(optOutKey(), '1'); setPrompt(false); }} className="h-8" data-testid="push-prompt-dismiss">Not now</Button>
            </div>
          </div>
          <button onClick={() => setPrompt(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close" data-testid="push-prompt-close"><X className="w-4 h-4" /></button>
        </div>
      )}
    </span>
  );
}
